<template>
  <div v-click-outside="clearItems" class="address-selector">
    <sd-textfield
      class="input--no-error-message input--has-clear-button"
      autocomplete="off"
      v-model="search"
      @input="handleTyping()"
      :has-error="hasError"
      @keydown="handleKeyDown"
    >
      Straße + Hausnummer + Ort eingeben
    </sd-textfield>
    <app-icon type="close" class="close-x" v-if="search" @click="handleInputDeletion()" />
    <div class="menu" :class="{ 'menu--above': isMenuAbove }">
      <div
        class="menu-item"
        :style="{ maxHeight: menuMaxHeight, display: isMenuVisible ? 'block' : 'none' }"
      >
        <sd-loading-dots
          class="loading-dots"
          v-if="isLoading"
          alt="Adressen werden gerade geladen"
        />
        <ul v-if="!isLoading && !hasError" ref="itemsList">
          <address-selector-item
            v-for="(item, index) in items"
            :key="index"
            :item="item"
            @selectItem="handleSelect"
            :selected="index === keyboardSelectedIndex"
          />
        </ul>
        <div v-if="!isLoading && !hasError" class="menu-footer">
          <div class="menu-footer__filter"></div>
          <sd-link href="#" @click="handleWithoutAddress()" primary
            >Du findest deine Adresse nicht?</sd-link
          >
          <sd-button has-full-width class="menu-footer__open-funnel" @click="handleButtonClick">
            <app-icon type="home_valid" />
            <span>Online-Preisrechner</span>
          </sd-button>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@mixin maxWidth($size) {
  @media screen and (max-width: $size) {
    @content;
  }
}

.address-selector {
  position: relative;
  padding: 0.5rem;

  .input {
    margin-bottom: 0;
  }
}

.close-x {
  position: absolute;
  top: 2.3rem;
  right: 1.1875rem;
  text-decoration: none;
  pointer-events: auto;
  cursor: pointer;
}

.loading-dots {
  padding: 0.75rem 1rem;
}

.menu-item {
  overflow: auto;
  overscroll-behavior: contain;
  background-color: var(--color-white);
  border-radius: 0.5rem;
  box-shadow: 0 -2px 6px rgb(0 0 0 / 4%), 0 2.5px 2.5px rgb(0 0 0 / 6%),
    0 8px 16px rgb(50 50 93 / 7%);

  @include maxWidth(768px) {
    &:first-child {
      border-top-left-radius: 0;
      border-top-right-radius: 0;
    }
  }

  ul {
    padding: 0.5rem 0.5rem;
    overflow-y: scroll;
    max-height: 13.5rem;
  }

  .menu-footer {
    position: relative;
    display: flex;
    flex-direction: column;
    padding: 0.5rem;

    a {
      padding: 0.75rem 1.5rem 1rem 1.5rem;
      box-shadow: 0 -4px 6px 0 #f7f7f7;
      margin: 0 -0.5rem;
    }

    &__filter {
      position: absolute;
      height: 20px;
      width: 100%;
      margin-left: -0.5rem;
      background-image: linear-gradient(
        rgba(255, 255, 255, 0),
        rgba(255, 255, 255, 0.5),
        rgba(255, 255, 255, 0.99)
      );
      top: -20px;
    }

    &__open-funnel {
      display: none;
      @include maxWidth(768px) {
        display: block;
      }
    }
  }
}

.menu {
  position: absolute;
  top: 5rem;
  @include maxWidth(768px) {
    top: 4.6rem;
  }
  left: 0;
  z-index: 20;
  width: 100%;
  max-width: 31.25rem;

  &.menu--above {
    top: auto;
    bottom: 5rem;
  }
}
</style>

<script>
import sdLoadingDots from '@smartdesign/vue/components/loading/loadingDots.vue';
import sdTextfield from '@smartdesign/vue/components/textfield/textfield.vue';
import sdLink from '@smartdesign/vue/components/link';
import AddressSelectorItem from './AddressSelectorItem.vue';
import AppIcon from './AppIcon.vue';
import addressService from '../services/addressService';
import debounce from '../utils/debounce';
import clamp from '../utils/clamp';
import compareGermanStrings from '../utils/compareGermanStrings';
import vClickOutside from 'click-outside-vue3';
import { defineComponent } from 'vue';
import SdButton from '@/components/SdButton.vue';

export default defineComponent({
  name: 'AddressSelector',
  components: {
    SdButton,
    sdTextfield,
    sdLink,
    sdLoadingDots,
    AddressSelectorItem,
    AppIcon,
  },
  directives: {
    clickOutside: vClickOutside.directive,
  },
  props: {
    modelValue: {
      type: Promise,
      required: true,
    },
    hasError: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  emits: ['update:modelValue', 'noResultsFound', 'search', 'handleEstimateButton', 'handleSelectSubmit', 'firstMatch'],
  data() {
    return {
      isLoading: false,
      items: [],
      search: '',
      isMenuAbove: false,
      menuMaxHeight: '',
      positionMenuInterval: 0,
      promiseResolved: true,
      keyboardSelectedIndex: -1,
    };
  },
  computed: {
    addressPromise: {
      get() {
        return this.modelValue;
      },
      set(value) {
        this.$emit('update:modelValue', value);
      },
    },
    isMenuVisible() {
      return this.isLoading || this.items.length > 0;
    },
  },
  watch: {
    isMenuVisible(isVisible) {
      if (isVisible) {
        this.positionMenu();
        this.positionMenuInterval = setInterval(this.positionMenu, 100);
      } else {
        clearInterval(this.positionMenuInterval);
      }
    },
  },
  created() {
    this.loadItemsDebounced = debounce(this.loadItems, 700);
  },
  mounted() {
    this.$el.querySelector('.input__textfield').addEventListener('focus', this.handleInputFocus);
    this.$emit('search', this.search);
  },
  unmounted() {
    clearInterval(this.positionMenuInterval); // just in case
  },
  methods: {
    async loadItems() {
      const search = this.search;

      if (!search || search.trim().length < 3) {
        if (this.promiseResolved) {
          this.addressPromise = Promise.resolve(null);
        } else {
          this.resolvePromise(null);
          this.promiseResolved = true;
        }

        this.isLoading = false;

        return;
      }
      this.isLoading = true;
      try {
        const items = await addressService.getMatchingAddresses(search);
        // use the response only if the user hasn't changed the search string during the backend call
        if (this.search === search) {
          this.items = items.map(addr => ({
            id: addr.id,
            label: this.getAddressString(addr),
            address: addr,
          }));
          this.keyboardSelectedIndex = 0;

          this.$emit('firstMatch', items[0]);

          const exactAddress = this.items.find(item =>
            compareGermanStrings(this.search, item.label),
          );

          if (exactAddress) {
            this.search = exactAddress.label;
            this.resolvePromise(exactAddress.address);
            this.promiseResolved = true;
          }

          if (!items.length) {
            this.$emit('noResultsFound');
          }
        }
      } catch (err) {
        console.error(`error calling autocomplete endpoint ${err}`); // eslint-disable-line no-console
      }
      if (this.search === search) {
        this.isLoading = false;
        this.resolvePromise(null);
        this.promiseResolved = true;
      }
    },
    handleButtonClick() {
      this.$emit('handleEstimateButton');
    },
    handleTyping() {
      if (this.promiseResolved) {
        this.promiseResolved = false;
        this.addressPromise = new Promise(res => {
          this.resolvePromise = res;
        });
      }
      this.clearItems();
      this.loadItemsDebounced();
      this.$emit('search', this.search);
    },
    handleInputDeletion() {
      this.search = '';
      this.handleTyping();
    },
    handleSelect(item, mTracking) {
      this.search = item.label;
      if (this.promiseResolved) {
        this.addressPromise = Promise.resolve(item.address);
        this.$emit('handleSelectSubmit', item.address, mTracking);
      } else {
        this.promiseResolved = true;
        this.resolvePromise(item.address);
      }
      this.clearItems();
    },
    handleKeydownSelect(item) {
      this.search = item.label;
      if (this.promiseResolved) {
        this.addressPromise = Promise.resolve(item.address);
      } else {
        this.promiseResolved = true;
        this.resolvePromise(item.address);
      }
    },
    handleWithoutAddress() {
      window.location.href = `${process.env.VUE_APP_VALUATION_RANGE_LANDING_PAGE_IW_WL}?m=estimate_landing_no_adress_found_estimate_step1`;
    },
    handleInputFocus() {
      this.$emit('search', this.search);
      this.loadItems();
    },
    clearItems() {
      this.items = [];
    },
    getAddressString(address) {
      const {
        display_name_token: {
          street_name: street,
          postal_code: zip,
          city_name: city,
          address_no: addressNo,
          address_no_ext: addressExt,
        },
      } = address;
      const num = addressNo?.toString();
      const ext = addressExt?.toString() || '';
      if (num) {
        return `${street} ${num}${ext}, ${zip} ${city}`;
      }
      return `${street}, ${zip} ${city}`;
    },
    positionMenu() {
      const rect = this.$el.getBoundingClientRect();

      const spaceAbove = rect.top - 20;
      const spaceBelow = window.innerHeight - rect.bottom - 20;

      // display menu below if there is enough space for at least 4 items
      // if not, display it where there is more space (above or below)
      this.isMenuAbove = spaceBelow >= 182 ? false : (spaceAbove > spaceBelow);
      this.menuMaxHeight = `${this.isMenuAbove ? spaceAbove : spaceBelow}px`;
    },
    handleKeyDown(event) {
      let index = this.keyboardSelectedIndex;

      if (!this.items) {
        return;
      }

      if (event.key === 'ArrowUp') {
        index--;
      } else if (event.key === 'ArrowDown') {
        index++;
      } else if (event.key === 'Enter') {
        this.handleSelect(this.items[this.keyboardSelectedIndex]);
      } else {
        return;
      }

      event.preventDefault();

      index = clamp(index, 0, this.items.length - 1);

      if (index !== this.keyboardSelectedIndex) {
        this.handleKeydownSelect(this.items[index]);
        this.keyboardSelectedIndex = index;
        this.scrollItemIntoView();
      }
    },
    scrollItemIntoView() {
      const containerRect = this.$refs.itemsList.getBoundingClientRect();
      const selectedRect = this.$refs.itemsList.children
        .item(this.keyboardSelectedIndex)
        .getBoundingClientRect();

      if (selectedRect.bottom > containerRect.bottom) {
        this.$refs.itemsList.scrollTop += selectedRect.bottom - containerRect.bottom + 40;
      }

      if (selectedRect.top < containerRect.top) {
        this.$refs.itemsList.scrollTop -= containerRect.top - selectedRect.top;
      }
    },
  },
});
</script>
